import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errText } from '../api';
import { useAuth } from '../auth';
import { Alert, SecretBox } from '../components/ui';
import type { SignupResponse } from '../types';

function AuthShell({ title, subtitle, children }: { title: string; subtitle: ReactNode; children: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · MsgGuard`;
  }, [title]);
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="brand brand-lg">
          <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden>
            <path d="M16 2 4 7v8c0 7.5 5.1 13.4 12 15 6.9-1.6 12-7.5 12-15V7z" fill="var(--accent)" />
            <path d="m11 16 3.5 3.5L21.5 12" stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>MsgGuard</span>
        </div>
        <h1>{title}</h1>
        <p className="muted auth-sub">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

const safeNext = (next: string | null) => (next && next.startsWith('/') && !next.startsWith('//') ? next : '/');

export function LoginPage() {
  const { me, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (me) return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/login', { method: 'POST', body: { email, password }, noRedirect: true });
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Moderate your platform's messages in real time.">
      <form onSubmit={submit} className="form">
        <Alert>{error}</Alert>
        <label className="field">
          <span>Email</span>
          <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <button className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="auth-foot">
        New to MsgGuard? <Link to="/signup">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function SignupPage() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ organization: '', name: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<SignupResponse | null>(null);

  if (me && !created) return <Navigate to="/" replace />;

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.password.length < 10) return setError('Password must be at least 10 characters');
    setBusy(true);
    setError(null);
    try {
      const res = await api<SignupResponse>('/api/auth/signup', { method: 'POST', body: form, noRedirect: true });
      setCreated(res);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <AuthShell title="Your account is ready" subtitle={<>Organization <strong>{created.organization.name}</strong> was created.</>}>
        <div className="form">
          <p>
            Here is your first API key (<strong>{created.api_key.name}</strong>). Use it to send messages to{' '}
            <code>POST /v1/messages</code>.
          </p>
          <SecretBox
            value={created.api_key.key}
            warning="Copy this key now and store it somewhere safe. It won't be shown again."
          />
          <button
            className="btn btn-primary btn-block"
            onClick={async () => {
              await refresh();
              navigate('/keys', { replace: true });
            }}
          >
            I've saved my key, continue
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Set up your organization and start moderating in minutes.">
      <form onSubmit={submit} className="form">
        <Alert>{error}</Alert>
        <label className="field">
          <span>Organization</span>
          <input required minLength={2} maxLength={120} value={form.organization} onChange={set('organization')} autoFocus />
        </label>
        <label className="field">
          <span>Your name</span>
          <input required maxLength={120} autoComplete="name" value={form.name} onChange={set('name')} />
        </label>
        <label className="field">
          <span>Work email</span>
          <input type="email" required autoComplete="email" value={form.email} onChange={set('email')} />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" required minLength={10} autoComplete="new-password" value={form.password} onChange={set('password')} />
          <span className="hint">At least 10 characters.</span>
        </label>
        <button className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>
      <p className="auth-foot">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
